import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useSnackbar } from 'notistack';

import Modal from '../../../../components/Modal/Modal';
import Input from '../../../../components/Input/Input';
import Text from '../../../../components/Text/Text';
import Button from '../../../../components/Button/Button';
import ButtonTypes from '../../../../constants/buttonTypes';
import Checkbox from '../../../../components/Checkbox/Checkbox';
import CustomSelect from '../../../../components/Select/CustomSelect';
import ConfirmModal from '../../../../components/ConfirmModal/ConfirmModal';
import Alert from '../../../../components/Alert/Alert';
import AlertType from '../../../../common/alertType';
import { IconArrowDown } from '../../../../assets/icon';
import {
  createSupplierQuote,
  getSupplierOrderSettings,
  placeSupplierOrder,
  updateSupplierOrderSettings
} from '../../../../services/supplierOrders';

const fieldClassName = 'flex flex-col gap-[5px]';
const hideBrokenImage = ({ currentTarget }) => {
  const image = currentTarget;
  image.hidden = true;
};
const recipientFields = [
  ['name', 'Recipient name'],
  ['phone_number', 'Phone'],
  ['address', 'Address'],
  ['address_2', 'Address line 2'],
  ['city', 'City'],
  ['state', 'State'],
  ['zip', 'ZIP'],
  ['country', 'Country'],
];
const defaultRecipient = {
  name: 'CO warehouse',
  address: '4699 Nautilus Ct S 503',
  address_2: 'M-F 9-3PM',
  city: 'Boulder',
  state: 'CO',
  country: 'US',
  zip: '80301',
  phone_number: '8889041411',
  is_shop_address: true,
};

const getErrorMessage = (error) => {
  const validationErrors = error?.errors ? Object.values(error.errors).flat() : [];
  return validationErrors[0]
    || error?.message
    || error?.data?.message
    || 'Supplier request failed.';
};

const SupplierOrderModal = ({
  visible, items, onClose, onChangeQuantity, onChangeWarehouse, onRemove, onOrdered
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [recipient, setRecipient] = useState(defaultRecipient);
  const [allowSplit, setAllowSplit] = useState(false);
  const [quote, setQuote] = useState();
  const [shippingIds, setShippingIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [confirmVisible, setConfirmVisible] = useState(false);
  const [error, setError] = useState();
  const [addressExpanded, setAddressExpanded] = useState(false);
  const [activeSupplier, setActiveSupplier] = useState('Turn14');
  const [shippingMethod, setShippingMethod] = useState('GROUND');
  const [couponCode, setCouponCode] = useState('');
  const [orderComment, setOrderComment] = useState('');

  const suppliers = useMemo(() => [...new Set(items.map(({ supplier }) => supplier))], [items]);
  const activeItems = useMemo(
    () => items.filter(({ supplier }) => supplier === activeSupplier),
    [items, activeSupplier]
  );
  const isPremier = activeSupplier === 'Premier';
  const isRockAuto = activeSupplier === 'RockAuto';
  const isComps = activeSupplier === 'Comps';
  const isKeystone = activeSupplier === 'Keystone';
  const isMeyer = activeSupplier === 'Meyer';
  const isEtristate = activeSupplier === 'Etristate';
  const isRcd = activeSupplier === 'RCD';
  const usesAutomaticFulfillment = isKeystone || isMeyer || isRcd;
  const usesDirectShippingMethod = isPremier || isComps || isEtristate;
  const supportsWarehouseSplit = activeSupplier === 'Turn14';

  const invalidateQuote = () => {
    setQuote(undefined);
    setShippingIds([]);
    setError(undefined);
  };

  useEffect(() => {
    if (!visible) {
      return;
    }
    setAddressExpanded(false);
    setActiveSupplier(items[0]?.supplier || 'Turn14');
    setShippingMethod(items[0]?.supplier === 'Comps' ? 'FEDH' : 'GROUND');
    setOrderComment('');
    setSettingsLoading(true);
    setError(undefined);
    getSupplierOrderSettings()
      .then((settings) => {
        setRecipient(settings.default_recipient || defaultRecipient);
        setCouponCode(settings.rockauto_coupon_code || '');
        invalidateQuote();
      })
      .catch((requestError) => setError(getErrorMessage(requestError)))
      .finally(() => setSettingsLoading(false));
  }, [visible]);

  const itemSubtotal = useMemo(() => activeItems.reduce(
    (total, item) => total + ((item.purchase_cost || 0) * item.quantity),
    0
  ), [activeItems]);
  const totalUnits = useMemo(() => activeItems.reduce(
    (total, item) => total + item.quantity,
    0
  ), [activeItems]);

  const selectedShippingCost = useMemo(() => (quote?.shipping_groups || []).reduce(
    (total, group, index) => {
      const option = group.options.find(
        ({ shipping_id: shippingId }) => `${shippingId}` === `${shippingIds[index]}`
      );
      return total + (option?.cost || 0);
    },
    0
  ), [quote, shippingIds]);
  const displayedSubtotal = quote?.items_subtotal ?? itemSubtotal;
  const orderTotal = displayedSubtotal + selectedShippingCost;

  const setRecipientField = (field, value) => {
    setRecipient({ ...recipient, [field]: value });
    invalidateQuote();
  };

  const handleQuote = () => {
    setLoading(true);
    setError(undefined);
    createSupplierQuote({
      supplier: activeSupplier,
      allow_split: allowSplit,
      comment: isRcd ? orderComment : undefined,
      recipient,
      items: activeItems.map((item) => ({
        external_item_id: item.external_item_id,
        mpn: item.mpn,
        brand: item.brand,
        type: item.type,
        quantity: item.quantity,
        warehouse_code: item.warehouse_code,
      })),
    })
      .then((result) => {
        setQuote(result);
        setShippingIds((result.shipping_groups || []).map(
          (group) => group.options?.[0]?.shipping_id
        ));
        setShippingMethod(result.default_shipping_method || 'GROUND');
        if (result.supplier === 'RockAuto') {
          setCouponCode(result.coupon_code || '');
        }
      })
      .catch((requestError) => setError(getErrorMessage(requestError)))
      .finally(() => setLoading(false));
  };

  const handlePlace = () => {
    setConfirmVisible(false);
    setLoading(true);
    setError(undefined);
    placeSupplierOrder({
      draft_id: quote.draft_id,
      shipping_ids: shippingIds,
      shipping_method: usesDirectShippingMethod ? shippingMethod : undefined,
      coupon_code: isRockAuto ? couponCode : undefined,
    })
      .then((result) => {
        const supplier = result.supplier || activeSupplier;
        enqueueSnackbar(
          result.message || `${supplier} order ${result.po_number} created`,
          { variant: 'success' }
        );
        onOrdered(result);
      })
      .catch((requestError) => {
        const message = getErrorMessage(requestError);
        setError(message);
        enqueueSnackbar(message, { variant: 'error' });
      })
      .finally(() => setLoading(false));
  };

  const handleChangeOrder = () => {
    setQuote(undefined);
    setShippingIds([]);
    setConfirmVisible(false);
    setError(undefined);
  };

  const saveDefaultAddress = () => {
    setLoading(true);
    setError(undefined);
    updateSupplierOrderSettings(recipient)
      .then(() => enqueueSnackbar('Default supplier address updated', { variant: 'success' }))
      .catch((requestError) => setError(getErrorMessage(requestError)))
      .finally(() => setLoading(false));
  };

  const isRecipientInvalid = !recipient.name || !recipient.address || !recipient.city
    || !recipient.state || !recipient.country || !recipient.zip || !recipient.phone_number;
  const recipientSummary = [
    recipient.name,
    recipient.address,
    recipient.address_2,
    [recipient.city, recipient.state, recipient.zip].filter(Boolean).join(' '),
    recipient.country,
  ].filter(Boolean).join(', ');
  const hasInvalidWarehouseStock = activeItems.some((item) => {
    if (item.supplier === 'RCD') return false;
    if (['Keystone', 'Meyer'].includes(item.supplier)) {
      return !item.total_available || item.total_available < item.quantity;
    }
    const selectedWarehouse = item.warehouses?.find(
      ({ code }) => `${code}` === `${item.warehouse_code}`
    );
    return !selectedWarehouse
      || (item.supplier !== 'RockAuto' && selectedWarehouse.quantity < item.quantity);
  });
  const selectedWarehouseCount = new Set(
    activeItems
      .filter(({ supplier }) => !['Keystone', 'Meyer', 'RCD'].includes(supplier))
      .map(({ warehouse_code: warehouseCode }) => warehouseCode)
      .filter(Boolean)
  ).size;
  const requiresSplit = selectedWarehouseCount > 1;
  const isWarehouseInvalid = hasInvalidWarehouseStock
    || (requiresSplit && ((supportsWarehouseSplit && !allowSplit) || isComps));
  const noShippingSelection = !usesDirectShippingMethod && !isRcd && Boolean(quote) && (
    !quote.shipping_groups?.length
    || shippingIds.filter(Boolean).length !== quote.shipping_groups.length
    || quote.shipping_groups.some((group, index) => group.options.find(
      ({ shipping_id: shippingId }) => `${shippingId}` === `${shippingIds[index]}`
    )?.cost === null)
  );
  const noDirectShippingMethod = usesDirectShippingMethod && Boolean(quote)
    && !shippingMethod.trim();
  const modalSubtitle = quote
    ? `PO ${quote.po_number} · ordered part ${quote.ordered_part_id}`
    : `${activeItems.length} item(s)`;
  let modalSaveText = 'Get shipping quote';
  if (usesDirectShippingMethod) modalSaveText = `Review ${activeSupplier} order`;
  if (isRcd) modalSaveText = 'Review email order';
  if (quote) {
    modalSaveText = isRcd ? 'Send order email' : `Place ${activeSupplier} order`;
  }
  const hasQuotedShipping = !usesDirectShippingMethod && Boolean(quote);
  let shippingSummary = hasQuotedShipping
    ? `$${selectedShippingCost.toFixed(2)}`
    : 'Calculated later';
  let totalLabel = hasQuotedShipping ? 'Grand total' : 'Current total';
  let totalSummary = hasQuotedShipping
    ? `$${orderTotal.toFixed(2)}`
    : `$${displayedSubtotal.toFixed(2)} + shipping`;
  if (isRcd) {
    shippingSummary = 'Confirmed by RCD';
    totalLabel = 'Order total';
    totalSummary = 'Confirmed by RCD';
  }
  let confirmText = `Place ${activeSupplier} order ${quote?.po_number || ''} `
    + `for $${orderTotal.toFixed(2)}?`;
  if (usesDirectShippingMethod) {
    confirmText = `Place ${activeSupplier} order ${quote?.po_number || ''} `
      + `for $${displayedSubtotal.toFixed(2)} plus shipping?`;
  }
  if (isRcd) {
    confirmText = `Send RCD order ${quote?.po_number || ''} to the configured email address?`;
  }

  return (
    <>
      <Modal
        visible={visible}
        toggleVisible={onClose}
        title={`Supplier order — ${activeSupplier}`}
        subTitle={modalSubtitle}
        saveText={modalSaveText}
        onSave={quote ? () => setConfirmVisible(true) : handleQuote}
        canSave={loading || settingsLoading || !activeItems.length || isRecipientInvalid
          || isWarehouseInvalid || noShippingSelection || noDirectShippingMethod}
        loading={loading || settingsLoading}
        additionalButton={quote ? (
          <Button
            text="Change order"
            type={ButtonTypes.LIGHT}
            disabled={loading}
            containerStyle="w-full sm:w-auto"
            onClick={handleChangeOrder}
          />
        ) : undefined}
      >
        <div className="w-[900px] max-w-full max-h-[70vh] overflow-auto customScroll px-[2px] sm:px-[4px]">
          {error && <Alert text={error} type={AlertType.warning} />}

          {suppliers.length > 1 && (
            <div className="mb-[18px]">
              <Text text="Order supplier" muted />
              <div className="mt-[5px]">
                <CustomSelect
                  options={suppliers.map((supplier) => ({ value: supplier, label: supplier }))}
                  value={{ value: activeSupplier, label: activeSupplier }}
                  isDisabled={Boolean(quote)}
                  onChange={(option) => {
                    setActiveSupplier(option.value);
                    setAllowSplit(false);
                    setShippingMethod(option.value === 'Comps' ? 'FEDH' : 'GROUND');
                    setOrderComment('');
                    invalidateQuote();
                  }}
                />
              </div>
              <Text
                text="Items from other suppliers remain in the cart and are ordered separately."
                muted
              />
            </div>
          )}

          {isRockAuto && (
            <div className="mb-[18px]">
              <Alert
                text={`RockAuto may take several minutes to calculate shipping or place the order.
                  Keep this window open and do not submit the request again.`}
                type={AlertType.important}
              />
            </div>
          )}

          {isRcd && (
            <div className="mb-[18px]">
              <Alert
                text={`RCD does not provide availability or pricing through an API.
                  The order request will be sent to RCD by email.`}
                type={AlertType.important}
              />
            </div>
          )}

          <div className="mb-[18px]">
            <Text text="Items" bold textStyle="text-[16px]" />
            <div className="flex flex-col gap-[8px] mt-[10px]">
              {activeItems.map((item) => (
                <div
                  key={`${item.supplier}-${item.external_item_id}`}
                  className={`grid grid-cols-1 xl:grid-cols-[1fr_210px_100px_110px_90px]
                    items-center gap-[10px] border-b border-whiteHover dark:border-[#1b263b] pb-[12px]`}
                >
                  <div className="flex items-center gap-[10px] min-w-0">
                    {item.thumbnail && (
                      <img
                        src={item.thumbnail}
                        alt={`${item.brand || item.supplier} ${item.mpn}`}
                        loading="lazy"
                        decoding="async"
                        className={`w-[52px] h-[52px] shrink-0 object-contain rounded-[6px]
                          border border-whiteHover dark:border-[#1b263b] bg-white`}
                        onError={hideBrokenImage}
                      />
                    )}
                    <div className="min-w-0">
                      <Text text={`${item.brand || 'Unknown'} — ${item.mpn}`} bold />
                      <Text text={`${item.supplier}: ${item.external_item_id}`} muted />
                    </div>
                  </div>
                  <div className={fieldClassName}>
                    <div className="xl:hidden"><Text text="Fulfillment" muted /></div>
                    {usesAutomaticFulfillment ? (
                      <div
                        className="min-h-[44px] flex items-center px-[12px] rounded-[6px]
                        bg-background-main dark:bg-[#1b263b]"
                      >
                        <div>
                          <Text text={isRcd ? 'Order by email' : `Selected by ${activeSupplier}`} bold />
                          <Text
                            text={isRcd
                              ? 'Availability and price confirmed by supplier'
                              : `${item.total_available || 0} available total`}
                            muted
                          />
                        </div>
                      </div>
                    ) : (
                      <CustomSelect
                        options={(item.warehouses || []).map((warehouse) => ({
                          value: warehouse.code,
                          label: `${warehouse.name} (${warehouse.quantity})${
                            warehouse.purchase_cost === undefined
                              ? ''
                              : ` · $${warehouse.purchase_cost.toFixed(2)}`
                          }`,
                          purchaseCost: warehouse.purchase_cost,
                        }))}
                        value={(item.warehouses || []).map((warehouse) => ({
                          value: warehouse.code,
                          label: `${warehouse.name} (${warehouse.quantity})${
                            warehouse.purchase_cost === undefined
                              ? ''
                              : ` · $${warehouse.purchase_cost.toFixed(2)}`
                          }`,
                        })).find(({ value }) => `${value}` === `${item.warehouse_code}`)}
                        isDisabled={Boolean(quote)}
                        onChange={(option) => {
                          onChangeWarehouse(
                            item.supplier,
                            item.external_item_id,
                            option.value,
                            option.purchaseCost
                          );
                          invalidateQuote();
                        }}
                      />
                    )}
                  </div>
                  <div className={fieldClassName}>
                    <div className="xl:hidden"><Text text="Quantity" muted /></div>
                    <Input
                      type="number"
                      min={1}
                      max={999}
                      value={item.quantity}
                      disabled={Boolean(quote)}
                      onChange={({ target: { value } }) => {
                        onChangeQuantity(
                          item.supplier,
                          item.external_item_id,
                          Math.max(1, parseInt(value, 10) || 1)
                        );
                        invalidateQuote();
                      }}
                    />
                  </div>
                  <Text
                    text={item.purchase_cost === null
                      ? 'Cost N/A'
                      : `$${(item.purchase_cost * item.quantity).toFixed(2)}`}
                    bold
                  />
                  <Button
                    text="Remove"
                    type={ButtonTypes.LIGHT_DANGER}
                    disabled={Boolean(quote)}
                    onClick={() => {
                      onRemove(item.supplier, item.external_item_id);
                      invalidateQuote();
                    }}
                  />
                </div>
              ))}
            </div>
          </div>

          {isRcd && (
            <div className="mb-[18px] flex flex-col gap-[5px]">
              <Text text="Order comment" muted />
              <Input
                multiline
                rows={4}
                value={orderComment}
                placeholder="Optional instructions for RCD"
                disabled={Boolean(quote)}
                onChange={({ target: { value } }) => {
                  setOrderComment(value);
                  invalidateQuote();
                }}
              />
            </div>
          )}

          <div className="mb-[18px] border border-whiteHover dark:border-[#415a77]/60 rounded-[8px]">
            <button
              type="button"
              aria-expanded={addressExpanded}
              aria-controls="supplier-order-address-fields"
              className="w-full flex items-center justify-between gap-[16px] p-[14px]
              text-left hover:bg-background-main dark:hover:bg-[#415a77]/20 rounded-[8px]"
              onClick={() => setAddressExpanded((expanded) => !expanded)}
            >
              <span className="min-w-0">
                <span className="block text-[16px] font-semibold text-text-main dark:text-secondary">
                  Delivery address
                </span>
                <span className="block mt-[3px] text-[12px] text-text-muted break-words">
                  {recipientSummary || 'Address is not configured'}
                </span>
              </span>
              <IconArrowDown
                className={`w-[18px] h-[18px] shrink-0 transition-transform duration-200
                ${addressExpanded ? 'rotate-180' : ''}`}
              />
            </button>
            {addressExpanded && (
              <div
                id="supplier-order-address-fields"
                className="px-[14px] pb-[14px] pt-[14px] border-t
                border-whiteHover dark:border-[#415a77]/60"
              >
                <div className="flex justify-end mb-[10px]">
                  <Button
                    text="Save as default"
                    type={ButtonTypes.LIGHT}
                    disabled={loading || settingsLoading || isRecipientInvalid || Boolean(quote)}
                    containerStyle="w-full sm:w-auto"
                    onClick={saveDefaultAddress}
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-[10px]">
                  {recipientFields.map(([field, label]) => (
                    <div className={fieldClassName} key={field}>
                      <Text text={label} muted />
                      <Input
                        value={recipient[field] || ''}
                        disabled={Boolean(quote)}
                        onChange={({ target: { value } }) => setRecipientField(field, value)}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {supportsWarehouseSplit && (
            <Checkbox
              name="allowSupplierSplit"
              text="Allow splitting between Turn14 warehouses when one warehouse cannot fulfill the whole order"
              isActive={allowSplit}
              disabled={Boolean(quote)}
              onChange={() => {
                setAllowSplit(!allowSplit);
                invalidateQuote();
              }}
            />
          )}
          {hasInvalidWarehouseStock && (
            <Alert
              text={usesAutomaticFulfillment
                ? `${activeSupplier} does not have enough total stock for every item.`
                : 'Select a warehouse that has enough stock for every item.'}
              type={AlertType.warning}
            />
          )}
          {supportsWarehouseSplit && requiresSplit && !allowSplit && (
            <Alert
              text="Items use different warehouses. Enable split shipment to request this quote."
              type={AlertType.warning}
            />
          )}
          {isComps && requiresSplit && (
            <Alert
              text="All items in a Comps order must use the same warehouse."
              type={AlertType.warning}
            />
          )}

          {quote && (
            <div className="mt-[20px]">
              <Text text="Shipping" bold textStyle="text-[16px]" />
              {quote.shipping_notice && (
                <div className="mt-[10px]">
                  <Alert text={quote.shipping_notice} type={AlertType.important} />
                </div>
              )}
              {isRcd && (
                <div className="mt-[10px]">
                  <Alert
                    text="RCD will confirm availability, item prices, and shipping after receiving the email."
                    type={AlertType.important}
                  />
                </div>
              )}
              {!isRcd && usesDirectShippingMethod && (
                <div className="mt-[10px] flex flex-col gap-[10px]">
                  <Alert
                    text={isPremier
                      ? `Premier calculates shipping after the order is submitted.
                        The amount below includes items only.`
                      : `${activeSupplier} does not provide a shipping quote before submission.
                        The amount below includes items only.`}
                    type={AlertType.important}
                  />
                  <div className={fieldClassName}>
                    <Text text="Shipping method" muted />
                    {isEtristate ? (
                      <Input
                        value={shippingMethod}
                        placeholder="2DAY"
                        disabled={loading}
                        onChange={({ target: { value } }) => setShippingMethod(value)}
                      />
                    ) : (
                      <CustomSelect
                        options={(quote.shipping_methods || []).map(({ code, label }) => ({
                          value: code,
                          label: `${label} (${code})`,
                        }))}
                        value={(quote.shipping_methods || []).map(({ code, label }) => ({
                          value: code,
                          label: `${label} (${code})`,
                        })).find(({ value }) => value === shippingMethod)}
                        menuPlacement="auto"
                        menuPortal
                        onChange={(option) => setShippingMethod(option.value)}
                      />
                    )}
                    {isEtristate && (
                      <Text text="You can override the default Etristate shipping code for this order." muted />
                    )}
                  </div>
                </div>
              )}
              {!isRcd && !usesDirectShippingMethod && !quote.shipping_groups?.length && (
                <Alert
                  text={`${activeSupplier} returned the quote without recognizable shipping options.
                    Check the backend API log before placing the order.`}
                  type={AlertType.warning}
                />
              )}
              {!usesDirectShippingMethod && !isRcd && (
                <div className="flex flex-col gap-[10px] mt-[10px]">
                  {quote.shipping_groups?.map((group, index) => {
                  const options = group.options.map((option) => ({
                    value: option.shipping_id,
                    label: `${option.carrier || ''} ${option.service || 'Shipping'}${
                      option.days_in_transit === null || option.days_in_transit === undefined
                        ? ''
                        : ` · ${option.days_in_transit} day${option.days_in_transit === 1 ? '' : 's'}`
                    }${
                      option.cost === null ? '' : ` — $${option.cost.toFixed(2)}`
                    }`.trim(),
                  }));
                  const selectedOption = group.options.find(
                    ({ shipping_id: shippingId }) => `${shippingId}` === `${shippingIds[index]}`
                  );
                  const shipmentLocation = group.location_name
                    ? `${group.location_name} (${group.location})`
                    : group.location;
                  return (
                    <div key={group.shipment} className={fieldClassName}>
                      <Text
                        text={`Shipment ${group.shipment}${shipmentLocation ? ` — ${shipmentLocation}` : ''}`}
                        muted
                      />
                      <CustomSelect
                        options={options}
                        value={options.find(({ value }) => `${value}` === `${shippingIds[index]}`)}
                        menuPlacement="auto"
                        menuPortal
                        onChange={(option) => setShippingIds(shippingIds.map(
                          (shippingId, shippingIndex) => (shippingIndex === index ? option.value : shippingId)
                        ))}
                      />
                      {selectedOption?.verbose_eta && (
                        <Text text={selectedOption.verbose_eta} muted />
                      )}
                    </div>
                  );
                  })}
                </div>
              )}
              {isRockAuto && (
                <div className="mt-[12px] flex flex-col gap-[10px]">
                  {(quote.messages || []).map((message) => (
                    <Alert key={message} text={message} type={AlertType.important} />
                  ))}
                  <div className={fieldClassName}>
                    <Text text="Coupon code" muted />
                    <Input
                      value={couponCode}
                      placeholder="Optional"
                      disabled={loading}
                      onChange={({ target: { value } }) => setCouponCode(value)}
                    />
                    <Text text="You can change the coupon without requesting a new shipping quote." muted />
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="mt-[20px] border border-whiteHover dark:border-[#415a77]/60 rounded-[8px] p-[14px]">
            <Text text="Order summary" bold textStyle="text-[16px]" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-[28px] gap-y-[8px] mt-[12px] text-[14px]">
              <div className="flex justify-between gap-[12px]">
                <span className="text-text-muted">Supplier</span>
                <strong>{activeSupplier}</strong>
              </div>
              <div className="flex justify-between gap-[12px]">
                <span className="text-text-muted">Items</span>
                <strong>
                  {activeItems.length}
                  {' line(s) · '}
                  {totalUnits}
                  {' unit(s)'}
                </strong>
              </div>
              {quote && (
                <>
                  <div className="flex justify-between gap-[12px]">
                    <span className="text-text-muted">PO</span>
                    <strong>{quote.po_number}</strong>
                  </div>
                  <div className="flex justify-between gap-[12px]">
                    <span className="text-text-muted">Ordered Part</span>
                    <strong>{quote.ordered_part_id}</strong>
                  </div>
                </>
              )}
            </div>
            <div
              className="border-t border-whiteHover dark:border-[#415a77]/60 mt-[12px]
              pt-[12px] flex flex-col gap-[8px] text-[14px]"
            >
              <div className="flex justify-between gap-[12px]">
                <span className="text-text-muted">Subtotal</span>
                <strong>{isRcd ? 'Confirmed by RCD' : `$${displayedSubtotal.toFixed(2)}`}</strong>
              </div>
              <div className="flex justify-between gap-[12px]">
                <span className="text-text-muted">Shipping</span>
                <strong>
                  {shippingSummary}
                </strong>
              </div>
              <div className="flex justify-between gap-[12px] text-[16px]">
                <span className="font-semibold">
                  {totalLabel}
                </span>
                <strong>
                  {totalSummary}
                </strong>
              </div>
            </div>
          </div>
        </div>
      </Modal>
      <ConfirmModal
        visible={confirmVisible}
        onCancel={() => setConfirmVisible(false)}
        onConfirm={handlePlace}
        confirmText={confirmText}
        confirmButtonText={isRcd ? 'Send email' : 'Place order'}
      />
    </>
  );
};

SupplierOrderModal.propTypes = {
  visible: PropTypes.bool,
  items: PropTypes.arrayOf(PropTypes.shape({
    supplier: PropTypes.string,
    external_item_id: PropTypes.string,
    mpn: PropTypes.string,
    brand: PropTypes.string,
    thumbnail: PropTypes.string,
    type: PropTypes.string,
    quantity: PropTypes.number,
    purchase_cost: PropTypes.number,
    total_available: PropTypes.number,
    warehouse_code: PropTypes.string,
    warehouses: PropTypes.arrayOf(PropTypes.shape({
      code: PropTypes.string,
      name: PropTypes.string,
      quantity: PropTypes.number,
      purchase_cost: PropTypes.number,
    })),
  })),
  onClose: PropTypes.func,
  onChangeQuantity: PropTypes.func,
  onChangeWarehouse: PropTypes.func,
  onRemove: PropTypes.func,
  onOrdered: PropTypes.func,
};

SupplierOrderModal.defaultProps = {
  visible: false,
  items: [],
  onClose: () => {},
  onChangeQuantity: () => {},
  onChangeWarehouse: () => {},
  onRemove: () => {},
  onOrdered: () => {},
};

export default SupplierOrderModal;
